import { useQuery } from "@tanstack/react-query";
import i18n from "@/i18n";
import { api } from "@/lib/api";

export const providersKey = ["auth", "providers"] as const;

/**
 * Asked of the API, not baked in: one bundle is promoted acc to production (ADR-0005), so an
 * unconfigured provider must simply not appear.
 */
export function useProviders() {
  return useQuery({
    queryKey: providersKey,
    queryFn: async () => {
      const { data, response } = await api.GET("/api/auth/providers");
      if (!data)
        throw new Error(
          i18n.t("session:local.errors.providersUnavailable", { status: response.status }),
        );
      return data.schemes ?? [];
    },
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Password plus TOTP. Comes back from the same endpoint as the SSO schemes but is not a link:
 * it never leaves this origin, so `SignIn` renders it as a form instead.
 */
export const LOCAL_SCHEME = "local";

/**
 * The fourth door. Like the local one it never leaves this origin - it is an address and a mail,
 * not a redirect - so `SignIn` renders it as a form and puts it first.
 */
export const INLOGLINK_SCHEME = "inloglink";

/**
 * `SignIn` renders only what is named here, so the Backoffice's client gets no dead button. Not a
 * gate, but in development both dev servers answer to both loopback names - see
 * docs/runbook/oauth-clients.md §Local development.
 */
const PROVIDER_SCHEMES = ["microsoft", LOCAL_SCHEME, INLOGLINK_SCHEME] as const;

export type ProviderScheme = (typeof PROVIDER_SCHEMES)[number];

/** Whether this host's answer is a scheme `SignIn` knows how to draw. */
export const isKnownProvider = (scheme: string): scheme is ProviderScheme =>
  (PROVIDER_SCHEMES as readonly string[]).includes(scheme);

/**
 * Read at render rather than held in a module constant: a constant would be built before
 * i18next has initialised and would freeze whatever the catalog said then.
 */
export const providerLabel = (scheme: ProviderScheme) =>
  i18n.t(`session:signIn.providers.${scheme}` as const);
